package com.klantenstop.web.pages.authentication.companyRegistration

import com.materialkobweb.components.toast.ToastManager
import com.klantenstop.web.data.repos.CompanyRepo
import com.materialkobweb.utils.ViewModel
import kotlinx.coroutines.FlowPreview
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.flow.launchIn
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

@OptIn(FlowPreview::class)
class CompanyRegistrationViewModel(
    private val companyRepo: CompanyRepo = CompanyRepo
) : ViewModel(), CompanyRegistrationContract {

    private val _state = MutableStateFlow(CompanyRegistrationContract.State())
    override val state = _state.asStateFlow()

    private val _effect = MutableSharedFlow<CompanyRegistrationContract.Effect>()
    override val effect = _effect.asSharedFlow()

    init {
        state.map { it.companyNumber }
            .filter { it.isNotEmpty() }
            .distinctUntilChanged()
            .debounce(500L)
            .onEach {
                sendIntent(CompanyRegistrationContract.Intent.ValidateCompanyNumber(it))
            }
            .launchIn(coroutineScope)
    }

    override fun sendIntent(intent: CompanyRegistrationContract.Intent) = coroutineScope.launch {
        when (intent) {
            is CompanyRegistrationContract.Intent.CompanyRegistration -> register()
            is CompanyRegistrationContract.Intent.UpdateCompanyNumber -> updateState { it.copy(companyNumber = intent.value) }
            is CompanyRegistrationContract.Intent.ValidateCompanyNumber -> validateCompanyNumber(intent.companyNumber)
            is CompanyRegistrationContract.Intent.CheckValidityOfCompanyNumber -> checkValidityOfCompanyNumber()
            is CompanyRegistrationContract.Intent.Reset -> updateState { CompanyRegistrationContract.State() }
            is CompanyRegistrationContract.Intent.RequestAccess -> requestAccess()
            is CompanyRegistrationContract.Intent.UpdateFormData -> updateState { it.copy(formData = intent.formData) }
            is CompanyRegistrationContract.Intent.UpdateRequestAccessFormData -> updateState { it.copy(requestAccessFormData = intent.formData) }
        }
    }

    private fun validateCompanyNumber(value: String) {
        // Simple validation:
        // - normalize the input by only allowing the first 2 digits be vowels and then be followed by 10 digits
        // - check if length is 10 characters (e.g., "BE0123456789")
        // - check if it starts with "BE" followed by 10 digits
        // - check if the format starts with 1 or 0 after "BE"

        val normalized = value
            .uppercase()
            .replace("BE", "")
            .replace(Regex("[^0-9]"), "")
            .let { "BE$it" }

        val isValid = Regex("^BE[0-9]{10}\$").matches(normalized) && (normalized[2] == '0' || normalized[2] == '1')
        updateState { it.copy(isCompanyNumberValid = isValid, showCompanyNumberError = if (isValid) null else "Controleer het btw-nummer, het bestaat uit BE gevolgd door een nul of één.") }
    }

    private fun requestAccess() {
        updateState { it.copy(showValidation = true) }

        // TODO: Implement request access logic
        // Steps to consider:
        // - Ask the user for additional information if needed (e.g., reason for access, contact details)
        // - Collect the company registration details from the state
        // - Send the request to the backend service
        // TODO: - Send an email to the company admin with the request details
        ToastManager.info("Toegangsaanvraag functionaliteit wordt binnenkort toegevoegd", title = "In ontwikkeling")
    }

    private suspend fun register() {
        updateState { it.copy(showValidation = true) }

        // TODO: Create the company and the user
        // TODO: After successful creation, link the user to the company as the company's admin
        // TODO: TBD: How do we want to verify the company registration? Do we want to create a service by using the address and mail a letter?
        // TODO: After successful creation and linking,
        //  - log the user in automatically
        //  - Navigate to the company detail page, or company dashboard where the admin can manage the company's:
        //      - details
        //      - users
        //      - status
        ToastManager.info("Bedrijfsregistratie functionaliteit wordt binnenkort toegevoegd", title = "In ontwikkeling")
    }

    private suspend fun checkValidityOfCompanyNumber() {
        val companyNumber = _state.value.companyNumber

        try {
//            val response = companyRepo.checkCompanyNumber(companyNumber = companyNumber)
//            if (response?.valid == true) {
//                updateState {
//                    it.copy(
//                        companyCheckResponse = response,
//                        isCompanyNumberValid = true,
//                        showCompanyNumberError = null
//                    )
//                }
//            } else {
//                updateState {
//                    it.copy(
//                        isCompanyNumberValid = false,
//                        showCompanyNumberError = "Het opgegeven bedrijfnummer is niet geldig."
//                    )
//                }
//            }
        } catch (e: Exception) {
            ToastManager.error(e.message ?: "Kon bedrijfsnummer niet verifiëren", title = "Verificatie mislukt")
        }
    }

    override fun emitEffect(effect: CompanyRegistrationContract.Effect) = coroutineScope.launch {
        _effect.emit(effect)
    }

    override fun updateState(block: (CompanyRegistrationContract.State) -> CompanyRegistrationContract.State) {
        _state.update(block)
    }
}
